import moment from '@g4/moment-utc';
import {
  DEPARTING,
  RETURNING,
  BASIC_BUNDLE,
  BONUS_BUNDLE,
  TOTAL_BUNDLE,
  ALLEGIANT_FIRST_BUNDLE,
  ALLEGIANT_FIRST,
  MAIN,
  NEW_FARE_TIER,
  ALLEGIANT_FIRST_NAME,
} from '../indexPage/constants';

const ancillaryTypesName = {
  priority_access: 'Priority Boarding',
  checked_bag: 'Checked Bags',
  carry_on: 'Carry On Bag',
  trip_flex: 'Trip Flex',
};

const ancillaryAffinity = {
  priority_access: 'PB',
  checked_bag: 'Bags/Checked Bags',
  carry_on: 'Bags/Carry On Bag',
  trip_flex: 'Trip Flex',
};

const getAncillaryKeyByName = ancillaryName => {
  return ancillaryName
    .split(ancillaryName.includes('-') ? '-' : ' ')
    .join('_')
    .toLowerCase();
};

// Helper function to remove "Allegiant" from bundle name for tracking
export const removeBrandFromBundleName = (bundleName) => {
  if (!bundleName) return '';
  return bundleName.replace(/\b[Aa]llegiant\s*/g, '').trim();
};

// Helper function to normalize bundle name for display (NEW_FARE → ALLEGIANT_FIRST_BUNDLE)
export const getDisplayBundleName = (bundleName) => {
  return bundleName === ALLEGIANT_FIRST_NAME ? ALLEGIANT_FIRST : bundleName;
};

export const getBundleRawProduct = (
  bundle,
  searchCriteria,
  affinityString,
  flight
) => {
  const {
    adultsCount,
    childrenCount,
    infantCount,
    lapInfantCount,
    tripType,
    departDate,
    returnDate,
    origin: marketOrigin,
  } = searchCriteria;
  const origin = flight.origin.code;
  const destination = flight.destination.code;
  const route = origin + destination;
  const totalPaxCount =
    adultsCount + childrenCount + infantCount + lapInfantCount;
  const bookingDaysOut = moment(departDate?.date).diff(moment(), 'days');
  const flightDirection = marketOrigin === origin ? 'D' : 'R';
  const flightDate = flight.id.split('-G4-')[0];
  const flightNumber = flight.id.split('-G4-')[1];
  const bundleName = removeBrandFromBundleName(bundle?.name);

  return {
    type: 'bundle',
    items: {
      item_id: `${origin + destination}|${getDisplayBundleName(bundleName)}`,
      item_name: getDisplayBundleName(bundleName),
      coupon: bundle?.discount?.value + '%' || '',
      discount: bundle?.savingsPerPerson?.amount,
      affiliation: 'WWW - Booking',
      index: 1,
      item_brand: (flight.operatedBy && flight.operatedBy?.carrier) || 'G4',
      item_category: 'Air Ancillary',
      item_category2: getDisplayBundleName(bundleName),
      item_category3: flightDirection,
      item_category4: route,
      item_category5: affinityString ? affinityString : 'Air',
      price:
        (bundle?.bundlePricePerPerson?.amount ?? 0) *
        (adultsCount + childrenCount + infantCount),
      item_variant: `${getDisplayBundleName(bundleName)}-${route}-${flightNumber}-${flightDate}`,
      quantity: 1,
      item_promotion_name: '',
      item_loyalty_points_discount: '',
      item_flight_booking_type: tripType,
      item_booking_start: departDate?.date,
      item_booking_end:
        tripType === 'ROUNDTRIP' ? returnDate?.date : departDate?.date,
      item_booking_days_out: bookingDaysOut,
      item_count_child: childrenCount,
      item_count_infant: infantCount,
      item_count_lap_child: lapInfantCount,
      item_count_adult: adultsCount,
      item_count_total: totalPaxCount,
      item_round_trip_discount: 0,
    },
  };
};

const getAncillaryRawProduct = (
  ancillary,
  flight,
  searchCriteria,
  affinityString
) => {
  const flightDate = flight.id.split('-G4-')[0];
  const flightNumber = flight.id.split('-G4-')[1];
  const {
    adultsCount,
    childrenCount,
    infantCount,
    lapInfantCount,
    tripType,
    departDate,
    returnDate,
    origin: marketOrigin,
    destination: marketDestination,
  } = searchCriteria;
  const flightDirection = marketOrigin === flight?.origin?.code ? 'D' : 'R';
  const route =
    flightDirection === 'D'
      ? marketOrigin + marketDestination
      : marketDestination + marketOrigin;
  const totalPaxCount =
    adultsCount + childrenCount + infantCount + lapInfantCount;
  const ancillaryKey = getAncillaryKeyByName(ancillary.name);
  const ancillaryName = ancillaryTypesName[ancillaryKey];
  const productAffinity = ancillaryAffinity[ancillaryKey];
  const itemId = `${ancillaryName}-${route}-${flightNumber}-${flightDate}`;
  const bookingDaysOut = moment(departDate?.date).diff(moment(), 'days');

  return {
    type: ancillaryName,
    direction: flightDirection,
    items: {
      item_id: itemId,
      item_name: ancillaryName,
      coupon: '',
      discount: ancillary.discount?.amount + '%' || '',
      affiliation: 'WWW - Booking',
      index: 1,
      item_brand: (flight.operatedBy && flight.operatedBy?.carrier) || 'G4',
      item_category: 'Air Ancillary',
      item_category2: productAffinity,
      item_category3: flightDirection,
      item_category4: route,
      item_category5: affinityString ? affinityString : 'Air',
      price:
        (ancillary?.price?.amount ?? 0) *
        (adultsCount + childrenCount + infantCount),
      item_variant: `${ancillaryName}-${route}-${flightNumber}-${flightDate}`,
      quantity: 1,
      item_promotion_name: '',
      item_loyalty_points_discount: '',
      item_flight_booking_type: tripType,
      item_booking_start: departDate?.date,
      item_booking_end:
        tripType === 'ROUNDTRIP' ? returnDate?.date : departDate?.date,
      item_booking_days_out: bookingDaysOut,
      item_count_child: childrenCount,
      item_count_infant: infantCount,
      item_count_lap_child: lapInfantCount,
      item_count_adult: adultsCount,
      item_count_total: totalPaxCount,
      item_round_trip_discount: 0,
    },
  };
};

export const getRawProductsWithIndexAndItems = (rawProducts) => {
  let index = 0;
  return rawProducts.reduce((acc, product) => {
    if (product.type) {
      product.items.index = ++index;
      acc.push(product.items);
    }
    return acc;
  }, []);
};

export const getBundleRawProductsForFlight = (
  selectedBundleOffer,
  searchCriteria,
  affinityString,
  flight
) => {
  const bundleRawProductItems = [];
  const rawProduct = getBundleRawProduct(
    selectedBundleOffer,
    searchCriteria,
    affinityString,
    flight
  );
  bundleRawProductItems.push(rawProduct);
  return bundleRawProductItems;
};

export const getBundleRawProducts = (
  selectedFlights,
  selectedBundles,
  bundleOffers,
  searchCriteria,
  affinityString
) => {
  const rawProducts = [];
  [DEPARTING, RETURNING].forEach((leg, index) => {
    const tier =
      index === 0 ? selectedBundles.outboundTier : selectedBundles.inboundTier;

    const selectedBundleForFlight = bundleOffers.find(
      (bundle) =>
        bundle.tier === tier &&
        (selectedFlights[leg]?.bundleReferences?.some(
          (reference) => reference === bundle.bundleReference
        ) || bundle.tier === NEW_FARE_TIER)
    );
    if (selectedBundleForFlight) {
      const rawProduct = getBundleRawProductsForFlight(
        selectedBundleForFlight,
        searchCriteria,
        affinityString,
        selectedFlights[leg]?.flight
      );
      rawProducts.push(...rawProduct);
    }
  });

  const rawProductItems = getRawProductsWithIndexAndItems(rawProducts);
  return rawProductItems;
};

export default getBundleRawProducts;
